import { useQuery } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Scheduling dashboard data sources — on-call assignments, special workdays
 * (holidays / remote days) and business-trip entries. Mirrors the legacy
 * DataCentral v4 contract (`modules/Oncall/services`, `modules/Dashboard/services`)
 * so the same REAL endpoints back the new @vnetwork/ui widgets.
 */

/* ── On-call assignments (week roster) ─────────────────────────────────── */
/** One on-call assignment — an employee on-call for a schedule on a given day. */
export interface OncallAssignment {
  oncall_assignment_id: number;
  oncall_schedule_id: number;
  oncall_schedule_name: string;
  oncall_rotation_id?: number;
  oncall_rotation_name?: string;
  employee_id: number;
  employee_name: string;
  department_name?: string;
  /** "YYYY-MM-DD" */
  oncall_date: string;
  oncall_start_time: string;
  oncall_end_time: string;
  oncall_assignment_status?: string;
  /** Set when this shift was reassigned/swapped away from the original person. */
  original_employee_id?: number;
  original_employee_name?: string;
  reassignment_reason?: string;
}

/**
 * On-call assignments in a date window (a week). Not UMA-gated. Wide page so a
 * week's worth of rows (~schedules × 7 days) comes back in one call.
 */
export function useOncallAssignments(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['dashboard', 'oncall-assignments', startDate, endDate],
    queryFn: async (): Promise<OncallAssignment[]> => {
      const res = await dataCentralApi.get<ResponseArray<OncallAssignment>>(
        `/oncall/assignments?page=1&page_size=500&start_date=${startDate}&end_date=${endDate}`,
      );
      // Legacy API sometimes answers `{ result }` instead of `{ results }`.
      const data = res.data as ResponseArray<OncallAssignment> & { result?: OncallAssignment[] };
      return data.results ?? data.result ?? [];
    },
  });
}

/* ── Special workdays (holidays / remote days) ─────────────────────────── */
/** A special workday for the month — holiday, remote-work day, Saturday off… */
export interface SpecialWorkday {
  holiday_id: number;
  holiday_name: string;
  /** "YYYY-MM-DD" */
  holiday_date: string;
  holiday_work_n_day?: number;
}

/**
 * Special workdays for a year/month (the legacy "WorkDays" / "ngày công" list).
 * Endpoint returns holidays, remote-work days and other schedule exceptions.
 */
export function useSpecialWorkdays(year: number, month: number) {
  return useQuery({
    queryKey: ['dashboard', 'special-workdays', year, month],
    queryFn: async (): Promise<SpecialWorkday[]> => {
      const res = await dataCentralApi.get<ResponseArray<SpecialWorkday>>(
        `/workdays/holiday/holiday-by-year-and-month?year=${year}&month=${month}`,
      );
      return res.data?.results ?? [];
    },
    staleTime: 5 * 60_000,
  });
}

/* ── Business trips & day-offs (today / tomorrow) ──────────────────────── */
export interface BusinessTripEntry {
  employee: { employee_id: number; employee_name: string; department_name?: string };
  business_type: 'business' | 'dayoff';
}

export interface BusinessTripsBuckets {
  today: BusinessTripEntry[];
  tomorrow: BusinessTripEntry[];
}

/**
 * Business trips / day-offs bucketed into today & tomorrow — the legacy
 * dashboard "Business" widget source.
 */
export function useBusinessTrips() {
  return useQuery({
    queryKey: ['dashboard', 'business-trips'],
    queryFn: async (): Promise<BusinessTripsBuckets> => {
      const res = await dataCentralApi.get<ResponseObject<BusinessTripsBuckets>>(
        '/dashboard/business_and_dayoffs',
      );
      return res.data?.result ?? { today: [], tomorrow: [] };
    },
  });
}
